import React, { useContext, useEffect, useState } from 'react'
import { PaymentProviderFields } from 'api/PaymentProvider/Types/PaymentProviderFields'
import { PaymentProviderAlternativeDto } from 'api/Wallet/Types/PaymentProviderAlternativeDto'
import { UpdateWalletDepositWithdrawalTransfer } from 'api/Wallet/Types/UpdateWalletDepositWithdrawalTransfer'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useAppSelector } from 'store'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { isEmpty } from 'utils/is-empty'
import { getCreditCreditOrFirstOneWallet } from 'utils/wallet'

import { CurrencyType } from '../../../../api/Wallet/Types/Wallet'
import { NPaymentsApi } from '../../../../utils/api/nPaymentsApi'
import {
  TransactionDetailsWalletDepositWithdrawalGeneralForm,
  TransactionDetailsWalletDepositWithdrawalGeneralProps,
} from './TransactionDetailsWalletDepositWithdrawalGeneralForm'

const useAlternativePaymentProvidersFetch = (
  props: TransactionDetailsWalletDepositWithdrawalGeneralProps
) => {
  const { transaction } = props

  const nPaymentsApi = useApiClient(NPaymentsApi)

  const [alternativesPaymentProviders, setAlternativesPaymentProviders] = useState<
    PaymentProviderAlternativeDto[]
  >([])

  useEffect(() => {
    const paymentProviderDetails = getCreditCreditOrFirstOneWallet(
      transaction.walletTransactions
    )?.paymentProviderTransaction
    if (paymentProviderDetails?.paymentProvider?.id && isEmpty(alternativesPaymentProviders)) {
      getAlternativePaymentProviders(
        paymentProviderDetails.paymentProvider.id,
        paymentProviderDetails.currency.id
      )
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getAlternativePaymentProviders = async (
    paymentProviderId: string,
    currencyId: CurrencyType
  ) => {
    const response = await nPaymentsApi.getAlternativesPaymentProviders(
      paymentProviderId,
      currencyId
    )
    setAlternativesPaymentProviders(response || [])
  }

  return { alternativesPaymentProviders, setAlternativesPaymentProviders }
}

const usePaymentProvidersFieldsFetch = (
  props: TransactionDetailsWalletDepositWithdrawalGeneralProps
) => {
  const { transaction } = props

  const [isLoading, setIsLoading] = useState(false)

  const walletsApiClient = useApiClient(WalletsApiClient)

  const [paymentProviderFields, setPaymentProviderFields] = useState<PaymentProviderFields[]>([])

  const getPaymentProviderFields = async (pspId: string) => {
    setIsLoading(true)
    const response = await walletsApiClient.getPaymentProviderField('withdrawals', {
      walletId: transaction.wallet?.id || transaction.walletTransactions[0].wallet.id,
      methodId: pspId,
      languageId: 'en',
      caller: 'bo',
    })
    setPaymentProviderFields(response.reply.form)
    setIsLoading(false)
  }

  return { paymentProviderFields, getPaymentProviderFields, isLoading }
}

const useTransactionWalletMutate = (
  props: TransactionDetailsWalletDepositWithdrawalGeneralProps & { forceRefresh(): void }
) => {
  const { transaction, forceRefresh } = props

  const setToast = useContext(ToastContext)
  const walletsApiClient = useApiClient(WalletsApiClient)

  const handleSubmit = async (
    values: UpdateWalletDepositWithdrawalTransfer,
    type: 'deposit' | 'withdraw'
  ) => {
    if (transaction) {
      const newValues = dataToSubmit(values)
      await updateTransaction(transaction.id, newValues, type)
    }
  }

  const updateTransaction = async (
    transactionId: string,
    values: UpdateWalletDepositWithdrawalTransfer,
    type: 'deposit' | 'withdraw'
  ) => {
    try {
      await walletsApiClient.updateWalletDepositWithdrawalTransfer(transactionId, values, type)
      setToast(successToast('Transaction updated successfully.'))
      forceRefresh()
    } catch (error) {
      console.error('Error updating transaction:', error)
      setToast(errorToast('Failed to update the transaction. Please try again.'))
    }
  }

  const dataToSubmit = (values: UpdateWalletDepositWithdrawalTransfer) => {
    const walletTransaction = getCreditCreditOrFirstOneWallet(transaction.walletTransactions)
    if (
      values.paymentProviderId !== walletTransaction?.paymentProviderTransaction?.paymentProvider.id
    ) {
      const { connectedToService, ...restValues } = values
      return restValues
    }

    return values
  }

  return { handleSubmit }
}

export const TransactionDetailsWalletDepositWithdrawalGeneralPage: React.FC<
  TransactionDetailsWalletDepositWithdrawalGeneralProps & { forceRefresh(): void }
> = (props) => {
  const { permissions, setActiveTab, notes, transaction, client } = props
  const { setAuditLogModal, setCustomizeModal, setProviderValidationModal } = props
  const {
    sendBankStatementNeededCCCoveredIBEmailRequest,
    sendBankStatementNeededDepositConfirmationEmailRequest,
  } = props

  const [isEdit, setEdit] = useState(false)

  const paymentProviders = useAppSelector((state) => state.transactions.paymentProviders)

  const { alternativesPaymentProviders } = useAlternativePaymentProvidersFetch(props)

  const { paymentProviderFields, getPaymentProviderFields, isLoading } =
    usePaymentProvidersFieldsFetch(props)

  const { handleSubmit } = useTransactionWalletMutate(props)

  return (
    <TransactionDetailsWalletDepositWithdrawalGeneralForm
      transaction={transaction}
      client={client}
      paymentProviders={paymentProviders}
      alternativesPaymentProviders={alternativesPaymentProviders}
      notes={notes}
      permissions={permissions}
      sendBankStatementNeededCCCoveredIBEmailRequest={
        sendBankStatementNeededCCCoveredIBEmailRequest
      }
      sendBankStatementNeededDepositConfirmationEmailRequest={
        sendBankStatementNeededDepositConfirmationEmailRequest
      }
      setAuditLogModal={setAuditLogModal}
      setCustomizeModal={setCustomizeModal}
      setProviderValidationModal={setProviderValidationModal}
      setActiveTab={setActiveTab}
      onSubmit={handleSubmit}
      isEdit={isEdit}
      setEdit={setEdit}
      paymentProviderFields={paymentProviderFields}
      getPaymentProviderFields={getPaymentProviderFields}
      loading={isLoading}
    />
  )
}
